import React, { useState, useEffect, useMemo } from 'react';
import { Mail, Send, X, Archive, Trash2, ChevronLeft, AlertCircle, AlertTriangle, Flag, Inbox as InboxIcon, MailOpen } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config';
import { useBusinessContext } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useDateFormatter } from '../utils/dateUtils';
import ViewerBanner from '../components/ViewerBanner';

const TOPIC_CATEGORIES = [
  'Valuation',
  'Exit Planning',
  'Financial Data',
  'Gap Analysis',
  'Tasks & Action Items',
  'Advisory Team',
  'General Question',
  'Other'
];

const PRIORITY_LEVELS = [
  { value: 'low', label: 'Low', color: 'bg-gray-500' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-500' }
];

const Inbox = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showCompose, setShowCompose] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const { currentBusiness, currentBusinessId, isAdvisor } = useBusinessContext();
  const { isViewer } = usePermissions();
  const { formatDateTime } = useDateFormatter();

  // Filters
  const [filterTopic, setFilterTopic] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'

  // Compose form
  const [composeForm, setComposeForm] = useState({
    subject: '',
    topic_category: 'General Question',
    message: '',
    priority: 'normal'
  });

  useEffect(() => {
    fetchMessages();
  }, [currentBusinessId, filterTopic, filterPriority, showArchived]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const businessId = currentBusiness?.id || currentBusinessId;

      const headers = { 'Authorization': `Bearer ${token}` };
      if (businessId) {
        headers['X-Business-Id'] = businessId;
      }

      const params = new URLSearchParams();
      if (filterTopic) params.append('topic', filterTopic);
      if (filterPriority) params.append('priority', filterPriority);
      if (showArchived) params.append('archived', 'true');

      const response = await axios.get(`${API_URL}/messages/?${params.toString()}`, { headers });
      setMessages(response.data.messages || []);
      setUnreadCount(response.data.unread_count || 0);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      setLoading(false);
    }
  };

  // Sort messages based on sortOrder
  const sortedMessages = useMemo(() => {
    const sorted = [...messages];
    sorted.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    return sorted;
  }, [messages, sortOrder]);

  const fetchMessageDetails = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      const businessId = currentBusiness?.id || currentBusinessId;

      const headers = { 'Authorization': `Bearer ${token}` };
      if (businessId) {
        headers['X-Business-Id'] = businessId;
      }

      const response = await axios.get(`${API_URL}/messages/${messageId}`, { headers });
      setSelectedMessage(response.data.message);
      setReplies(response.data.replies || []);

      // Refresh message list to update read status and unread count
      fetchMessages();
    } catch (error) {
      console.error('Failed to fetch message details:', error);
    }
  };

  const handleCompose = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const token = localStorage.getItem('token');
      const businessId = currentBusiness?.id || currentBusinessId;

      if (!businessId) {
        alert('Business context required to send messages. Please navigate to a specific business first.');
        setSending(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'X-Business-Id': businessId
      };

      await axios.post(`${API_URL}/messages/`, composeForm, { headers });

      // Reset form and close modal
      setComposeForm({
        subject: '',
        topic_category: 'General Question',
        message: '',
        priority: 'normal'
      });
      setShowCompose(false);
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    setSending(true);

    try {
      const token = localStorage.getItem('token');
      // Use business_id from the message being replied to (not from context)
      const businessId = selectedMessage.business_id;

      const headers = {
        'Authorization': `Bearer ${token}`,
        'X-Business-Id': businessId
      };

      await axios.post(`${API_URL}/messages/`, {
        subject: `Re: ${selectedMessage.subject}`,
        topic_category: selectedMessage.topic_category,
        message: replyText,
        priority: selectedMessage.priority,
        parent_message_id: selectedMessage.id
      }, { headers });

      setReplyText('');
      fetchMessageDetails(selectedMessage.id);
      fetchMessages();
    } catch (error) {
      console.error('Failed to send reply:', error);
      alert('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleArchive = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      // Use business_id from the selected message if available
      const businessId = selectedMessage?.business_id || currentBusiness?.id || currentBusinessId;

      const headers = {
        'Authorization': `Bearer ${token}`,
        'X-Business-Id': businessId
      };

      await axios.put(`${API_URL}/messages/${messageId}`, {
        is_archived: !showArchived
      }, { headers });

      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
    } catch (error) {
      console.error('Failed to archive message:', error);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      const token = localStorage.getItem('token');
      // Use business_id from the selected message if available
      const businessId = selectedMessage?.business_id || currentBusiness?.id || currentBusinessId;

      const headers = {
        'Authorization': `Bearer ${token}`,
        'X-Business-Id': businessId
      };

      await axios.delete(`${API_URL}/messages/${messageId}`, { headers });

      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
    } catch (error) {
      console.error('Failed to delete message:', error);
      alert('Failed to delete message');
    }
  };

  const getPriorityBadge = (priority) => {
    const level = PRIORITY_LEVELS.find(p => p.value === priority) || PRIORITY_LEVELS[1];
    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${level.color}`}>
        {level.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-gray-500">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Viewer Banner */}
      <div className="px-4 sm:px-6 pt-4 sm:pt-6">
        <ViewerBanner />
      </div>

      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2 flex-wrap">
              <Mail className={unreadCount > 0 ? "text-orange-500" : "text-blue-600"} size={20} />
              Inbox
              {unreadCount > 0 && (
                <span className="bg-orange-500 text-white text-xs sm:text-sm px-3 py-1.5 rounded-full font-bold shadow-lg animate-pulse">
                  {unreadCount} unread
                </span>
              )}
            </h1>
            {currentBusiness && (
              <p className="text-xs sm:text-sm text-gray-600 mt-1">{currentBusiness.business_name}</p>
            )}
          </div>
          {!isViewer && (
            <button
              onClick={() => setShowCompose(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 min-h-[44px] w-full sm:w-auto"
            >
              <Send size={18} />
              New Message
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          <select
            value={filterTopic}
            onChange={(e) => setFilterTopic(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px]"
          >
            <option value="">All Topics</option>
            {TOPIC_CATEGORIES.map(topic => (
              <option key={topic} value={topic}>{topic}</option>
            ))}
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px]"
          >
            <option value="">All Priorities</option>
            {PRIORITY_LEVELS.map(level => (
              <option key={level.value} value={level.value}>{level.label}</option>
            ))}
          </select>

          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px]"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>

          <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show Archived
          </label>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Message List */}
        <div className={`${selectedMessage ? 'hidden md:block md:w-1/3' : 'w-full'} border-r border-gray-200 bg-white overflow-y-auto`}>
          {sortedMessages.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <InboxIcon size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No messages found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {sortedMessages.map((message) => {
                // Check if there are any unread messages in the thread
                const hasUnread = message.has_unread_in_thread ?? !message.is_read;
                return (
                <div
                  key={message.id}
                  onClick={() => fetchMessageDetails(message.id)}
                  className={`p-4 cursor-pointer transition-all duration-200 ${
                    hasUnread
                      ? 'bg-orange-50 border-l-4 border-orange-500 shadow-md hover:bg-orange-100 hover:shadow-lg'
                      : 'hover:bg-gray-50'
                  } ${selectedMessage?.id === message.id ? 'bg-blue-100 border-l-4 border-blue-500' : ''}`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    {/* Unread icon indicator */}
                    <div className="pt-1">
                      {hasUnread ? (
                        <Mail className="text-orange-500" size={20} strokeWidth={2.5} />
                      ) : (
                        <MailOpen className="text-gray-400" size={20} strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {getPriorityBadge(message.priority)}
                        <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                          {message.topic_category}
                        </span>
                        {hasUnread ? (
                          <span className="text-xs px-2.5 py-1 bg-orange-500 text-white rounded-full font-bold shadow-sm">
                            UNREAD
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded">
                            Read
                          </span>
                        )}
                      </div>
                      <h3 className={`text-sm ${hasUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'} truncate`}>
                        {message.subject}
                      </h3>
                    </div>
                  </div>
                  <p className={`text-sm ${hasUnread ? 'text-gray-800 font-medium' : 'text-gray-600'} mb-2 line-clamp-2 ml-8`}>
                    {message.message}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500 ml-8">
                    <span className={hasUnread ? 'font-semibold text-gray-700' : ''}>
                      From: {message.sender_display} - {message.business_name}
                    </span>
                    <span>{formatDateTime(message.created_at)}</span>
                  </div>
                  {message.reply_count > 0 && (
                    <div className="mt-2 text-xs text-blue-600 ml-8">
                      {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Message Detail */}
        {selectedMessage && (
          <div className="flex-1 flex flex-col bg-white overflow-hidden">
            {/* Message Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="md:hidden flex items-center gap-2 text-gray-600 hover:text-gray-900"
                >
                  <ChevronLeft size={20} />
                  Back
                </button>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getPriorityBadge(selectedMessage.priority)}
                    <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                      {selectedMessage.topic_category}
                    </span>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedMessage.subject}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleArchive(selectedMessage.id)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                    title={showArchived ? 'Unarchive' : 'Archive'}
                  >
                    <Archive size={18} />
                  </button>
                  {selectedMessage.sender_id === parseInt(localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).id : 0) && (
                    <button
                      onClick={() => handleDelete(selectedMessage.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Message Thread */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Original Message */}
              <div className={`rounded-lg p-4 border ${
                selectedMessage.sender_role === 'advisor'
                  ? 'bg-blue-50 border-blue-200 mr-8'
                  : 'bg-gray-50 border-gray-200 ml-8'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{selectedMessage.sender_display}</span>
                    {selectedMessage.sender_role === 'advisor' && (
                      <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Advisor</span>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">{formatDateTime(selectedMessage.created_at)}</span>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>

              {/* Replies */}
              {replies.map((reply) => {
                const isAdvisor = reply.sender_role === 'advisor';
                return (
                  <div
                    key={reply.id}
                    className={`rounded-lg p-4 border ${
                      isAdvisor
                        ? 'bg-blue-50 border-blue-200 mr-8'
                        : 'bg-gray-50 border-gray-200 ml-8'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{reply.sender_display}</span>
                        {isAdvisor && (
                          <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Advisor</span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">{formatDateTime(reply.created_at)}</span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{reply.message}</p>
                  </div>
                );
              })}
            </div>

            {/* Reply Form */}
            {!isViewer && (
              <div className="border-t border-gray-200 p-4">
                <form onSubmit={handleReply} className="space-y-3">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none text-sm sm:text-base"
                    rows="3"
                    required
                  />
                  <button
                    type="submit"
                    disabled={sending || !replyText.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 min-h-[44px] w-full sm:w-auto"
                  >
                    {sending ? 'Sending...' : 'Send Reply'}
                  </button>
                </form>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">New Message</h2>
              <button
                onClick={() => setShowCompose(false)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCompose} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject *
                </label>
                <input
                  type="text"
                  value={composeForm.subject}
                  onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Topic Category *
                  </label>
                  <select
                    value={composeForm.topic_category}
                    onChange={(e) => setComposeForm({ ...composeForm, topic_category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    {TOPIC_CATEGORIES.map(topic => (
                      <option key={topic} value={topic}>{topic}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority *
                  </label>
                  <select
                    value={composeForm.priority}
                    onChange={(e) => setComposeForm({ ...composeForm, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    {PRIORITY_LEVELS.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  value={composeForm.message}
                  onChange={(e) => setComposeForm({ ...composeForm, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                  rows="6"
                  required
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowCompose(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inbox;
